import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { NotificationType } from 'types'
import z from 'zod'

import useErrorMessage from '@hooks/use-error-message'

import { Tabs, TabsList, TabsTrigger } from '@shadcn/tabs'

import { useMutationNotificationMarkAllRead } from '@api/notification/notification.mutations'
import { queryNotificationCount } from '@api/notification/notification.queries'

import AsyncButton from '@/components/async-button/async-button'
import NotificationList from '@/components/notifications/notification-list'
import type { CursorPaginationState } from '@/components/table/cursor-pagination'
import { DEFAULT_PAGE_SIZE } from '@/lib/table'

import SectionHeader from '../-components/section-layout/section-header'
import SectionLayout from '../-components/section-layout/section-layout'

const schema = z.object({
  status: z.enum(['read', 'unread', 'all']).default('all'),
  type: z
    .enum([
      NotificationType.CompanyGradeChange,
      NotificationType.NewCoverageAfterInsistence,
      NotificationType.NoCoverageAfterInsistence,
      NotificationType.NewPolicyImported,
    ])
    .optional()
    .catch(undefined),
})

export const Route = createFileRoute('/app/notifications')({
  component: RouteComponent,
  validateSearch: schema,
})

function RouteComponent() {
  const { t } = useTranslation()
  const { status, type } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [pagination, setPagination] = useState<CursorPaginationState>({
    pageSize: DEFAULT_PAGE_SIZE,
  })

  const handleStatusChange = (value: string) => {
    setPagination({ pageSize: pagination.pageSize })
    navigate({
      to: '.',
      search: (previous) => ({
        ...previous,
        status: value as 'read' | 'unread' | 'all',
      }),
    })
  }

  const handleTypeChange = (value: string) => {
    const castValue = value as NotificationType | 'all' | undefined
    setPagination({ pageSize: pagination.pageSize })
    navigate({
      to: '.',
      search: (previous) => ({
        ...previous,
        type: !castValue || castValue === 'all' ? undefined : castValue,
      }),
    })
  }

  return (
    <SectionLayout title={t('notifications.title')} className="overflow-hidden">
      <div className="flex h-full grow flex-col divide-y overflow-hidden">
        <SectionHeader title={t('notifications.title')}>
          <MarkAllAsReadButton status={status} type={type} />
        </SectionHeader>
        <div className="flex flex-1 flex-col gap-4 overflow-hidden p-2">
          <div className="flex flex-wrap items-center gap-4">
            <Tabs value={status} onValueChange={handleStatusChange}>
              <TabsList>
                <TabsTrigger value="all">
                  {t('notifications.status.all')}
                </TabsTrigger>
                <TabsTrigger value="read">
                  {t('notifications.status.read')}
                </TabsTrigger>
                <TabsTrigger value="unread">
                  {t('notifications.status.unread')}
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <Tabs value={type ?? ''} onValueChange={handleTypeChange}>
              <TabsList>
                <TabsTrigger value="">
                  {t('notifications.type.all')}
                </TabsTrigger>
                {Object.values(NotificationType).map((notificationType) => (
                  <TabsTrigger key={notificationType} value={notificationType}>
                    {t(`notifications.type-long.${notificationType}`)}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
          <div className="flex h-full justify-center overflow-hidden">
            <NotificationList
              status={status}
              type={type}
              pagination={pagination}
              onPaginationChange={setPagination}
            />
          </div>
        </div>
      </div>
    </SectionLayout>
  )
}

interface MarkAllAsReadButtonProps {
  status: 'read' | 'unread' | 'all'
  type?: NotificationType
}

const MarkAllAsReadButton = ({ status, type }: MarkAllAsReadButtonProps) => {
  const { t } = useTranslation()
  const errorMessage = useErrorMessage()

  const { data: countData } = useQuery(queryNotificationCount({ status, type }))
  const markAllRead = useMutationNotificationMarkAllRead()

  const hasUnread = (countData?.unread ?? 0) > 0

  const markAllAsRead = () =>
    markAllRead.mutate(undefined, {
      onError: (error) => {
        const message = errorMessage({ error })
        toast.error(message)
      },
      onSuccess: () => {
        toast.success(t('notifications.markAllAsRead.success'))
      },
    })

  return (
    <AsyncButton
      size="xs"
      variant="primary"
      disabled={!hasUnread}
      onClick={markAllAsRead}
    >
      {t('notifications.markAllAsRead.trigger')}
    </AsyncButton>
  )
}
